// MySearchBar.tsx (Fixed with focus persistence)
import { useState, useEffect, useRef } from "react";
import { Search, X } from "lucide-react";

export interface MySearchBarProps {
  onSearch: (value: string) => void;
  value?: string;
  placeholder?: string;
  loading?: boolean;
  debounceMs?: number;
  className?: string;
}

const MySearchBar: React.FC<MySearchBarProps> = ({
  onSearch,
  value: externalValue,
  placeholder = "Search...",
  loading = false,
  debounceMs = 500,
  className = "",
}) => {
  const [inputValue, setInputValue] = useState(externalValue || "");
  const [isFocused, setIsFocused] = useState(false);
  const debounceTimeoutRef = useRef<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync with external value when it changes
  useEffect(() => {
    if (externalValue !== undefined) {
      setInputValue(externalValue);
    }
  }, [externalValue]);

  // Debounce effect
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = window.setTimeout(() => {
      onSearch(inputValue);
    }, debounceMs);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [inputValue, onSearch, debounceMs]);

  // Maintain focus after re-renders
  useEffect(() => {
    if (isFocused && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isFocused, loading]); // Re-focus when loading state changes

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
  };

  const handleClear = () => {
    setInputValue("");
    onSearch("");
    // Keep focus on input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSearchClick = () => {
    // Cancel any pending debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = undefined;
    }
    // Trigger search immediately
    onSearch(inputValue);
    // Keep focus on input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchClick();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    // Small delay to allow click events on buttons to fire
    setTimeout(() => {
      setIsFocused(false);
    }, 100);
  };

  return (
    <div className={`relative w-full ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="
            w-full pl-3 pr-10 py-2.5 text-md rounded-xl
            border border-gray-200 dark:border-gray-700
            bg-white dark:bg-gray-800
            text-gray-900 dark:text-white
            placeholder:text-gray-500 dark:placeholder:text-gray-400
            focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent
            transition-all
          "
        />
        
        {inputValue && (
          <button
            onClick={handleClear}
            onMouseDown={(e) => e.preventDefault()} // Prevent blur
            className="
              absolute right-8 top-1/2 -translate-y-1/2
              p-1 rounded-full
              text-gray-400 hover:text-red-500
              dark:text-gray-500 dark:hover:text-red-400
              transition-colors
              hover:bg-gray-100 dark:hover:bg-gray-700
              z-10
            "
            title="Clear search"
          >
            <X size={16} />
          </button>
        )}

        <Search 
          onClick={handleSearchClick}
          onMouseDown={(e) => e.preventDefault()} // Prevent blur
          className={`
            absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5
            transition-all cursor-pointer z-10
            ${loading 
              ? 'animate-pulse text-gray-400 dark:text-gray-500 cursor-not-allowed'
              : isFocused 
                ? 'text-purple-500 hover:text-purple-600' 
                : 'text-gray-400 hover:text-purple-500'
            }
          `}
          title="Search"
        />
      </div>
      
      {/* Optional: Show loading indicator in input */}
      {loading && (
        <div className="absolute right-12 top-1/2 -translate-y-1/2">
          <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

export default MySearchBar;